/* ============================================================
   cust-persona-attribution.js — global persona attribution layer
   ============================================================
   Loaded on every storefront page via layout/theme.liquid. The job:
   make _persona_id attribution session-wide instead of page-bound, so
   the Store Persona dashboard's Conversions tile matches reality.

   The bug this fixes: the persona-product-offering-v4 section writes
   _persona_id to its own /cart/add.js calls, but if a visitor lands
   on /pages/tammy-2-0, taps a product, gets bounced to a regular PDP,
   and adds to cart from THAT page, no attribution gets written. Meta's
   pixel catches the conversion; our cart-attribute writer misses it.
   Today's gap: Meta = 5 conversions, dashboard = 1.

   Architecture (three layers):

   1. DETECT a persona on landing. Sources, in priority order:
      a) URL param ?utm_persona=tammy_2_0 (or ?persona=...)
      b) sessionStorage from a prior page-load in the same tab
      c) Cookie from a prior visit within the 7-day window (matches
         Meta's default click-attribution window)

      The persona-product-offering-v4 section ALSO writes the persona_id
      to sessionStorage + cookie on its own page-load, so visitors who
      arrive via the persona page get tagged automatically.

   2. PERSIST: sessionStorage (tab lifetime) + cookie (7-day TTL).
      sessionStorage gives clean session semantics; the cookie covers
      a tab close + reopen within the attribution window.

   3. INTERCEPT every /cart/add.js (and /cart/update.js, /cart/change.js)
      call sitewide, both fetch() and XMLHttpRequest. Inject _persona_id
      into every item's properties if not already set. Existing tags
      from the persona section win — we never overwrite.

   Idempotent: safe to load twice, safe to call addItem from multiple
   sources, safe to run alongside the persona-section's own writer. */

(function () {
  var STORAGE_KEY = "viori_persona_attribution";
  var COOKIE_KEY = "viori_persona_attribution";
  var COOKIE_DAYS = 7;
  var INSTALLED_FLAG = "__viori_persona_attribution_installed__";

  // Meta Conversions API (CAPI) click-ID capture (Phase 3a.next).
  // When a visitor arrives from a Meta ad, the URL carries ?fbclid=...
  // We construct an _fbc value in the format Meta expects ("fb.1.<ms>.<fbclid>")
  // and persist it as a cookie for 90 days (matches Meta's pixel behavior).
  // The _fbp cookie is set by Meta's pixel itself when it loads — we just
  // read it from document.cookie. Both get injected into /cart/add.js line
  // item properties alongside _persona_id, then the order-paid webhook
  // pulls them off the order and includes them in the CAPI Purchase event
  // for Meta's user-matching.
  var FBC_COOKIE_KEY = "_fbc";
  var FBP_COOKIE_KEY = "_fbp";
  var FBC_DAYS = 90;
  var FBC_STORAGE_KEY = "viori_fbc_value";

  // Idempotency guard — don't double-wrap fetch / XHR if the snippet
  // gets included twice by accident (e.g. dev injects an extra copy).
  if (window[INSTALLED_FLAG]) return;
  window[INSTALLED_FLAG] = true;

  // --- 1a. Detect persona from URL on initial load ---------------
  // ?utm_persona= or ?persona= takes precedence over stored values.
  // Once detected, persisted to sessionStorage + cookie immediately
  // so a same-tab reload doesn't re-parse the URL.
  try {
    var u = new URL(window.location.href);
    var fromUrl = u.searchParams.get("utm_persona") || u.searchParams.get("persona");
    if (fromUrl && isValidPersonaId(fromUrl)) {
      persistPersona(fromUrl);
    }

    // --- 1b. Detect Meta click ID (fbclid) -----------------------
    // Construct _fbc in the canonical format: "fb.1.<unix_ms>.<fbclid>"
    // Persist to cookie + sessionStorage for cross-page session use. If
    // Meta's own pixel set _fbc earlier, we don't overwrite it (their
    // value already encodes the click chain correctly).
    var fbclid = u.searchParams.get("fbclid");
    if (fbclid && fbclid.length > 0 && fbclid.length < 256) {
      var existingFbc = readCookie(FBC_COOKIE_KEY);
      if (!existingFbc) {
        var fbcVal = "fb.1." + Date.now() + "." + fbclid;
        try {
          sessionStorage.setItem(FBC_STORAGE_KEY, fbcVal);
        } catch (e) { /* no-op */ }
        try {
          var exp = new Date(Date.now() + FBC_DAYS * 86400000).toUTCString();
          document.cookie = FBC_COOKIE_KEY + "=" + encodeURIComponent(fbcVal) +
            ";expires=" + exp + ";path=/;SameSite=Lax";
        } catch (e) { /* no-op */ }
      }
    }
  } catch (e) { /* URL parse failed — fall through to storage */ }

  // --- 1c. Detect persona from DOM on page load ----------------
  // persona-product-offering(-v4).liquid renders its root element with
  // data-persona-id="<sp_persona handle>". If a visitor lands on the
  // persona page and never ATCs from the buy box (they navigate to a
  // PDP or collection and ATC there), we still need _persona_id on the
  // cart. Reading the data attribute here and persisting it to session
  // + cookie means the fetch/XHR interceptors below pick it up on any
  // ATC anywhere on the site for the rest of the 7-day window.
  function detectPersonaFromDOM() {
    try {
      // Multiple sections on a persona page can each emit data-persona-id.
      // One may be un-baked (page.handle with dashes, e.g. "tammy-2-0"),
      // another may be the canonical sp_persona handle ("tammy_2_0").
      // Iterate and take the first value that passes our snake_case
      // validator — that's the authoritative persona ID.
      var els = document.querySelectorAll("[data-persona-id]");
      for (var i = 0; i < els.length; i++) {
        var id = els[i].getAttribute("data-persona-id");
        if (id && isValidPersonaId(id)) {
          persistPersona(id);
          activePersona = id;
          // Beacon the fbclid landing to the worker so order-paid can
          // recover persona attribution if persistPersona's storage
          // doesn't survive checkout (incognito, ITP, ad blocker). Only
          // fires when fbclid is present in the URL — the only case
          // where the server-side lookup is useful.
          maybeLogPersonaLanding(id);
          return;
        }
      }
    } catch (e) { /* no-op */ }
  }

  // Send a one-shot beacon to the worker recording (fbclid, persona_id,
  // landing_path). text/plain Blob body avoids CORS preflight (sendBeacon
  // can't set arbitrary headers anyway). Worker stores in
  // persona_landing_log for 30 days, indexed by fbclid. Idempotent on the
  // worker side — repeated landings overwrite via INSERT OR REPLACE.
  function maybeLogPersonaLanding(personaId) {
    try {
      var u = new URL(window.location.href);
      var fbclid = u.searchParams.get("fbclid");
      if (!fbclid) return;
      var url = "https://api.viori.storepersona.com/api/landing/persona-log";
      var payload = JSON.stringify({
        fbclid: fbclid,
        persona_id: personaId,
        landing_path: window.location.pathname,
      });
      if (navigator.sendBeacon) {
        var blob = new Blob([payload], { type: "text/plain" });
        navigator.sendBeacon(url, blob);
      } else if (typeof fetch === "function") {
        fetch(url, {
          method: "POST",
          mode: "no-cors",
          keepalive: true,
          headers: { "Content-Type": "text/plain" },
          body: payload,
        }).catch(function () { /* swallow */ });
      }
    } catch (e) { /* swallow */ }
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", detectPersonaFromDOM, { once: true });
  } else {
    detectPersonaFromDOM();
  }

  // --- 2. Load active persona (URL > session > cookie) ------------
  var activePersona = readPersona();

  // --- 3. Intercept cart-add APIs --------------------------------
  // fetch() wrapper — most modern themes use this. Patches the body
  // before passing through to the real fetch. We only touch URLs
  // ending in /cart/add.js, /cart/change.js, /cart/update.js so
  // non-cart fetches are untouched.
  if (typeof window.fetch === "function") {
    var originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      try {
        var url = typeof input === "string" ? input : (input && input.url) || "";
        if (isCartWriteUrl(url) && init) {
          init = injectPersonaIntoInit(init, getActivePersona());
        }
      } catch (e) { /* swallow — never break cart ops */ }
      return originalFetch(input, init);
    };
  }

  // XMLHttpRequest wrapper — older themes + Shopify SDK still use it.
  // Patches send() to rewrite the body if the open() target was a
  // cart-write URL.
  try {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__viori_cart_write = isCartWriteUrl(url || "");
      } catch (e) { /* no-op */ }
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this.__viori_cart_write && body) {
          var persona = getActivePersona();
          // Inject even without persona — we may still have _fbc/_fbp.
          if (persona || getActiveFbc() || getActiveFbp()) {
            body = injectPersonaIntoBody(body, persona);
          }
        }
      } catch (e) { /* no-op */ }
      return origSend.call(this, body);
    };
  } catch (e) { /* XHR not patchable — fetch path covers most theme code */ }

  // ---------- Helpers ---------------------------------------------

  function isCartWriteUrl(url) {
    if (!url) return false;
    // Normalize: strip query string + anchor, lowercase.
    var path = String(url).split("?")[0].split("#")[0].toLowerCase();
    return path.indexOf("/cart/add.js") !== -1
        || path.indexOf("/cart/change.js") !== -1
        || path.indexOf("/cart/update.js") !== -1;
  }

  // Inject _persona_id + _meta_fbc/_meta_fbp into a fetch init object's
  // body. Returns a new init with patched body + Content-Type guaranteed.
  // Pass-through on any parse / serialize failure so the cart op still runs.
  // Runs as long as ANY identifier is available (persona OR fbc OR fbp) —
  // a customer hitting the site from a Meta ad without first landing on
  // the persona page still gets _meta_fbc/_meta_fbp captured for CAPI.
  function injectPersonaIntoInit(init, persona) {
    if (!init || !init.body) return init;
    if (!persona && !getActiveFbc() && !getActiveFbp()) return init;
    var newBody = injectPersonaIntoBody(init.body, persona);
    if (newBody === init.body) return init;
    // Clone init so we don't mutate the caller's object. Ensure JSON
    // content-type since we now know the body is JSON.
    var newInit = {};
    for (var k in init) if (Object.prototype.hasOwnProperty.call(init, k)) newInit[k] = init[k];
    newInit.body = newBody;
    var headers = init.headers || {};
    var hasCT = false;
    if (headers instanceof Headers) {
      hasCT = headers.has("Content-Type") || headers.has("content-type");
      if (!hasCT) {
        var h2 = new Headers(headers);
        h2.set("Content-Type", "application/json");
        newInit.headers = h2;
      }
    } else if (Array.isArray(headers)) {
      for (var i = 0; i < headers.length; i++) {
        if (String(headers[i][0]).toLowerCase() === "content-type") { hasCT = true; break; }
      }
      if (!hasCT) newInit.headers = headers.concat([["Content-Type", "application/json"]]);
    } else {
      for (var hk in headers) if (hk.toLowerCase() === "content-type") { hasCT = true; break; }
      if (!hasCT) {
        var h3 = {};
        for (var hk2 in headers) if (Object.prototype.hasOwnProperty.call(headers, hk2)) h3[hk2] = headers[hk2];
        h3["Content-Type"] = "application/json";
        newInit.headers = h3;
      }
    }
    return newInit;
  }

  // Inject _persona_id + _meta_fbc + _meta_fbp into a request body.
  // Handles JSON strings, already-parsed objects, URLSearchParams, and
  // FormData. Returns the patched body or the original body if we couldn't
  // safely patch.
  function injectPersonaIntoBody(body, persona) {
    var fbc = getActiveFbc();
    var fbp = getActiveFbp();
    // JSON string (most common Shopify pattern).
    if (typeof body === "string") {
      try {
        var parsed = JSON.parse(body);
        var patched = injectPersonaIntoJsonObject(parsed, persona);
        if (patched) return JSON.stringify(patched);
      } catch (e) { /* not JSON — try form-encoded below */ }
      // form-encoded ?items[0][id]=...&items[0][properties][_persona_id]=...
      try {
        var sp = new URLSearchParams(body);
        if (sp.has("id")) {
          if (persona && !sp.has("properties[_persona_id]")) sp.set("properties[_persona_id]", persona);
          if (fbc && !sp.has("properties[_meta_fbc]")) sp.set("properties[_meta_fbc]", fbc);
          if (fbp && !sp.has("properties[_meta_fbp]")) sp.set("properties[_meta_fbp]", fbp);
          return sp.toString();
        }
      } catch (e) { /* not form-encoded either */ }
      return body;
    }
    // Plain object body.
    if (body && typeof body === "object" && !(body instanceof FormData) && !(body instanceof URLSearchParams) && !(body instanceof Blob)) {
      var patched2 = injectPersonaIntoJsonObject(body, persona);
      return patched2 || body;
    }
    // FormData body — Shopify's classic cart form posts. Mutate in place;
    // skip per-key if already present.
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      try {
        if (persona && !body.has("properties[_persona_id]")) body.append("properties[_persona_id]", persona);
        if (fbc && !body.has("properties[_meta_fbc]")) body.append("properties[_meta_fbc]", fbc);
        if (fbp && !body.has("properties[_meta_fbp]")) body.append("properties[_meta_fbp]", fbp);
      } catch (e) { /* no-op */ }
      return body;
    }
    // URLSearchParams body.
    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      try {
        if (body.has("id")) {
          if (persona && !body.has("properties[_persona_id]")) body.set("properties[_persona_id]", persona);
          if (fbc && !body.has("properties[_meta_fbc]")) body.set("properties[_meta_fbc]", fbc);
          if (fbp && !body.has("properties[_meta_fbp]")) body.set("properties[_meta_fbp]", fbp);
        }
      } catch (e) { /* no-op */ }
      return body;
    }
    return body;
  }

  // Take a JSON-shaped cart-add body and inject _persona_id (+ _meta_fbc
  // and _meta_fbp for Meta CAPI matching) wherever it makes sense.
  // Returns the patched object, or null if no items were found.
  function injectPersonaIntoJsonObject(obj, persona) {
    if (!obj || typeof obj !== "object") return null;
    var fbc = getActiveFbc();
    var fbp = getActiveFbp();
    // Multi-item: { items: [{id, quantity, properties}, ...] }
    if (Array.isArray(obj.items)) {
      for (var i = 0; i < obj.items.length; i++) {
        var it = obj.items[i];
        if (!it || typeof it !== "object") continue;
        it.properties = it.properties || {};
        if (persona && !it.properties._persona_id) it.properties._persona_id = persona;
        if (fbc && !it.properties._meta_fbc) it.properties._meta_fbc = fbc;
        if (fbp && !it.properties._meta_fbp) it.properties._meta_fbp = fbp;
      }
      return obj;
    }
    // Single-item: { id, quantity, properties }
    if (obj.id != null) {
      obj.properties = obj.properties || {};
      if (persona && !obj.properties._persona_id) obj.properties._persona_id = persona;
      if (fbc && !obj.properties._meta_fbc) obj.properties._meta_fbc = fbc;
      if (fbp && !obj.properties._meta_fbp) obj.properties._meta_fbp = fbp;
      return obj;
    }
    return null;
  }

  // Read the active Meta click ID. sessionStorage takes precedence (set
  // when we constructed _fbc from fbclid), then the canonical _fbc cookie.
  function getActiveFbc() {
    try {
      var ss = sessionStorage.getItem(FBC_STORAGE_KEY);
      if (ss) return ss;
    } catch (e) { /* fall through */ }
    var ck = readCookie(FBC_COOKIE_KEY);
    return ck || null;
  }

  // Read Meta's browser pixel ID. Meta's pixel sets this cookie on every
  // page load; we just relay it.
  function getActiveFbp() {
    return readCookie(FBP_COOKIE_KEY) || null;
  }

  // Active-persona getter that re-reads storage on every call. Lets a
  // persona-page navigation (which writes to sessionStorage from its
  // own inline script) take effect immediately, without forcing a
  // page reload of the global attribution script.
  function getActivePersona() {
    var fresh = readPersona();
    if (fresh) activePersona = fresh;
    return activePersona;
  }

  function readPersona() {
    try {
      var ss = sessionStorage.getItem(STORAGE_KEY);
      if (isValidPersonaId(ss)) return ss;
    } catch (e) { /* storage blocked — fall through */ }
    var ck = readCookie(COOKIE_KEY);
    if (isValidPersonaId(ck)) return ck;
    return null;
  }

  function persistPersona(id) {
    if (!isValidPersonaId(id)) return;
    // First-write-only — prevents attribution poisoning when visitors browse
    // across persona landing pages within a session. The first persona stays
    // sticky for the entire session + 7-day cookie window. Without this guard,
    // a Tammy lead who browses Keisha gets _persona_id overwritten to keisha,
    // inflating Keisha's ROAS and stealing Tammy's attribution.
    var existing = null;
    try { existing = sessionStorage.getItem(STORAGE_KEY); } catch (e) { /* no-op */ }
    if (!existing) {
      try { existing = readCookie(COOKIE_KEY); } catch (e) { /* no-op */ }
    }
    if (existing && isValidPersonaId(existing)) return; // already set — keep it
    try {
      sessionStorage.setItem(STORAGE_KEY, id);
      sessionStorage.setItem('viori_persona_first_touch_at', new Date().toISOString());
    } catch (e) { /* no-op */ }
    try {
      var expires = new Date(Date.now() + COOKIE_DAYS * 86400000).toUTCString();
      document.cookie = COOKIE_KEY + "=" + encodeURIComponent(id) +
        ";expires=" + expires + ";path=/;SameSite=Lax";
      document.cookie = "viori_persona_first_touch_at=" + encodeURIComponent(new Date().toISOString()) +
        ";expires=" + expires + ";path=/;SameSite=Lax";
    } catch (e) { /* no-op */ }
  }

  function readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  // Loose validator — persona IDs are lowercase snake_case 1-64 chars.
  // Prevents garbage in storage from showing up in cart properties.
  function isValidPersonaId(s) {
    return typeof s === "string" && /^[a-z][a-z0-9_]{0,63}$/.test(s);
  }

  // --- 4. Write _offer cart attribute after /cart/add.js success ----
  // Parse URL offer param (comma-separated), intersect with canonical
  // strings, write _offer as JSON array. Includes bamboo auto-add for
  // "Free Bamboo Holder". Once per page load, 60-min offer_ts window,
  // try/catch + 1 retry. Never blocks the cart op.
  var CANONICAL_OFFERS = ['Free Shipping', 'Free Bamboo Holder', '50% Off'];
  var BAMBOO_VARIANT_ID = 31983025455203;
  var __offerWritten = false;
  var __bambooAdded = false;
  function maybeWriteOfferAttribute() {
    if (__offerWritten) return;
    try {
      var sp = new URLSearchParams(window.location.search);
      var offer = sp.get('offer');
      var offerTs = sp.get('offer_ts');
      if (!offer) return;
      if (!offerTs || (Date.now() - Number(offerTs)) >= 3600000) return;
      // Split on comma, intersect with canonical strings
      var parts = offer.split(',');
      var matched = [];
      for (var i = 0; i < parts.length; i++) {
        var trimmed = parts[i].trim();
        for (var j = 0; j < CANONICAL_OFFERS.length; j++) {
          if (trimmed === CANONICAL_OFFERS[j]) { matched.push(trimmed); break; }
        }
      }
      if (matched.length === 0) return;
      __offerWritten = true;
      var body = JSON.stringify({ attributes: { _offer: JSON.stringify(matched) } });
      var opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body };
      originalFetch('/cart/update.js', opts).then(function () {
        maybeAutoAddBamboo(matched);
      }).catch(function () {
        setTimeout(function () {
          try { originalFetch('/cart/update.js', opts).then(function () {
            maybeAutoAddBamboo(matched);
          }).catch(function () {}); } catch (e) {}
        }, 2000);
      });
    } catch (e) { /* never break the cart op */ }
  }

  // Auto-add bamboo holder when "Free Bamboo Holder" offer is active.
  // Guard: skip for logged-in returning customers (window.__vioriOrdersCount > 0).
  // Guests still get the auto-add (checkout-recognized returners are a known gap).
  function maybeAutoAddBamboo(matched) {
    if (__bambooAdded) return;
    try {
      if (matched.indexOf('Free Bamboo Holder') === -1) return;
      // Logged-in returning customer guard
      if (window.__vioriOrdersCount > 0) return;
      __bambooAdded = true;
      // Check if bamboo is already in cart
      originalFetch('/cart.js', { method: 'GET', headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var lines = cart.items || [];
          for (var i = 0; i < lines.length; i++) {
            if (lines[i].variant_id === BAMBOO_VARIANT_ID &&
                lines[i].properties && lines[i].properties._offer_gift === 'bamboo') {
              return; // already in cart
            }
          }
          // Add bamboo with _offer_gift property
          return originalFetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: BAMBOO_VARIANT_ID, quantity: 1, properties: { '_offer_gift': 'bamboo' } }] })
          });
        })
        .then(function (res) {
          if (res && res.ok) {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('rebuy:cart:refresh', { bubbles: true }));
            if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
              try { window.Rebuy.SmartCart.show(); } catch (e) {}
            }
          }
        })
        .catch(function (e) { console.warn('[persona-attr] bamboo auto-add failed', e); });
    } catch (e) { console.warn('[persona-attr] bamboo auto-add error', e); }
  }

  // Hook into the fetch wrapper: after a successful /cart/add.js, fire
  // the offer attribute writer. We re-wrap here (post-persona-inject
  // wrapper is already in place above) to tap the response.
  if (typeof window.fetch === 'function') {
    var _personaFetch = window.fetch;
    window.fetch = function (input, init) {
      var result = _personaFetch.call(window, input, init);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var path = String(url).split('?')[0].split('#')[0].toLowerCase();
        if (path.indexOf('/cart/add.js') !== -1) {
          result.then(function (res) {
            if (res && res.ok) maybeWriteOfferAttribute();
          }).catch(function () {});
        }
      } catch (e) { /* no-op */ }
      return result;
    };
  }

  // Expose a public hook so the persona-section's own inline script
  // can tell the attribution layer to refresh its cache, AND so we can
  // debug from console (window.vioriPersona).
  window.vioriPersona = {
    set: function (id) { persistPersona(id); activePersona = id; },
    get: function () { return getActivePersona(); },
    clear: function () {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
      try {
        document.cookie = COOKIE_KEY + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      } catch (e) {}
      activePersona = null;
    },
  };
})();
